import { useEffect, useMemo, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "../../../lib/api";

function todayIso() { return new Date().toISOString().slice(0, 10); }
function isoDaysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

/**
 * Daily Settlement Report — per-restaurant accounting view.
 *   - orders, gross sales, merchant payout, Bizil cut
 *   - cash to collect from rider (COD) + prepaid amount + CDC pay-to-merchant
 *   - filter by date range and (optionally) one restaurant
 *   - CSV download
 */
export default function DailySettlementReport() {
    const [restaurants, setRestaurants] = useState([]);
    const [dateFrom, setDateFrom] = useState(isoDaysAgo(6));
    const [dateTo, setDateTo] = useState(todayIso());
    const [restaurantId, setRestaurantId] = useState("");
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => { api.get("/admin/restaurants").then(({ data }) => setRestaurants(data || [])); }, []);

    const query = useMemo(() => {
        const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
        if (restaurantId) p.set("restaurant_id", restaurantId);
        return p.toString();
    }, [dateFrom, dateTo, restaurantId]);

    const run = async () => {
        if (!dateFrom || !dateTo) return toast.error("Pick a date range");
        setBusy(true);
        try {
            const { data } = await api.get(`/admin/reports/daily-settlement?${query}`);
            setData(data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to run report"); } finally { setBusy(false); }
    };
    useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

    const download = async () => {
        setDownloading(true);
        try {
            const res = await api.get(`/admin/reports/daily-settlement?${query}&format=csv`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `daily-settlement_${dateFrom}_to_${dateTo}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("CSV downloaded");
        } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); } finally { setDownloading(false); }
    };

    const t = data?.totals;

    return (
        <div className="space-y-4">
            <div className="bg-card border border-border rounded-2xl p-4">
                <h2 className="text-lg font-bold">Daily Settlement</h2>
                <p className="text-xs text-muted-foreground mt-1">Per-restaurant breakdown of payable, Bizil cut and rider cash for the chosen window.</p>

                <div className="grid sm:grid-cols-4 gap-3 mt-4 text-sm">
                    <label className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">From</span>
                        <input data-testid="rpt-from" type="date" className="inp" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">To</span>
                        <input data-testid="rpt-to" type="date" className="inp" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Restaurant</span>
                        <select data-testid="rpt-restaurant" className="inp" value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)}>
                            <option value="">All restaurants</option>
                            {restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                    </label>
                    <div className="flex items-end gap-2">
                        <button data-testid="rpt-run" onClick={run} disabled={busy} className="flex-1 bg-foreground text-background rounded-xl py-2.5 font-bold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-1">
                            {busy && <Loader2 size={14} className="animate-spin" />} Run
                        </button>
                        <button data-testid="rpt-csv" onClick={download} disabled={downloading || !data} className="border border-border rounded-xl px-3 py-2.5 text-sm font-bold inline-flex items-center gap-1 disabled:opacity-50">
                            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} CSV
                        </button>
                    </div>
                </div>
                <style>{`.inp{background:hsl(var(--muted));border-radius:0.75rem;padding:0.55rem 0.75rem;outline:none;font-size:0.875rem;width:100%}`}</style>
            </div>

            {t && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <Stat label="Orders" v={t.orders} />
                    <Stat label="Gross sales" v={inr(t.gross_sales)} />
                    <Stat label="Merchant payout" v={inr(t.merchant_payout)} klass="text-emerald-700" />
                    <Stat label="Bizil cut" v={inr(t.admin_earn)} klass="text-violet-700" />
                    <Stat label="COD to collect" v={inr(t.cod_to_collect)} klass="text-amber-700" />
                    <Stat label="Prepaid" v={inr(t.prepaid_amount)} klass="text-sky-700" />
                    <Stat label="CDC pay to merchant" v={inr(t.cdc_to_pay_merchant)} klass="text-violet-700" />
                </div>
            )}

            <div className="bg-card border border-border rounded-2xl overflow-x-auto">
                <table className="w-full text-sm min-w-[760px]">
                    <thead className="bg-muted/60 text-left text-[11px] uppercase tracking-wider">
                        <tr>
                            <th className="px-4 py-3">Restaurant</th>
                            <th className="px-4 py-3 text-right">Orders</th>
                            <th className="px-4 py-3 text-right">Gross</th>
                            <th className="px-4 py-3 text-right">Payout</th>
                            <th className="px-4 py-3 text-right">Bizil cut</th>
                            <th className="px-4 py-3 text-right">COD</th>
                            <th className="px-4 py-3 text-right">Prepaid</th>
                            <th className="px-4 py-3 text-right">CDC → Merchant</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data?.rows || []).map((r) => (
                            <tr key={r.restaurant_id} data-testid={`rpt-row-${r.restaurant_id}`} className="border-t border-border">
                                <td className="px-4 py-3 font-semibold">{r.restaurant_name}</td>
                                <td className="px-4 py-3 text-right">{r.orders}</td>
                                <td className="px-4 py-3 text-right">{inr(r.gross_sales)}</td>
                                <td className="px-4 py-3 text-right text-emerald-700 font-semibold">{inr(r.merchant_payout)}</td>
                                <td className="px-4 py-3 text-right text-violet-700 font-semibold">{inr(r.admin_earn)}</td>
                                <td className="px-4 py-3 text-right text-amber-700">{inr(r.cod_to_collect)}</td>
                                <td className="px-4 py-3 text-right text-sky-700">{inr(r.prepaid_amount)}</td>
                                <td className="px-4 py-3 text-right text-violet-700">{inr(r.cdc_to_pay_merchant)}</td>
                            </tr>
                        ))}
                        {data && (data?.rows || []).length === 0 && <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">No orders in this window.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Stat({ label, v, klass }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-3">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">{label}</p>
            <p className={`text-xl font-bold mt-1 ${klass || ""}`}>{v}</p>
        </div>
    );
}
